import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 0 }

import { computed, inject, onBeforeUnmount, onMounted, provide, Ref, ref, useSlots, watch } from 'vue';
import { OverviewCategory, OverviewFilter, OverviewResult } from '@/types';
import { addToState, filterItems, removeFromState } from '@/utils';

const URL = '/.rest/api/v1/overview';
const LIMIT = 12;


export default /*#__PURE__*/_defineComponent({
  __name: 'LzOverview',
  props: {
    uuid: {},
    workspace: {}
  },
  setup(__props: any) {

const props = __props;


const slots = useSlots();

const el = ref<HTMLElement>(null);
const items = ref([]);
const loading = ref(false);
const error = ref(false);
const filters: Ref<OverviewFilter[]> = ref([]);
const filtersOpen = ref(false);

const contextPath = inject('contextPath');
const siteName = inject('siteName');
const lang = inject('lang');

const filterString = computed(() => selectedFiltersBuffer.value.filter(x => x.length).join(','));

const selectedFilters = ref([]);
const selectedFiltersBuffer = ref([]);
const result: Ref<OverviewResult> = ref(null);
const endReached = ref(false);
let offset = 0;
let controller = new AbortController();

provide('registerOverviewFilter', registerFilter);

watch(endReached, () => {
    if (endReached.value) {
        if (result.value && result.value.total > items.value.length) {
            offset += LIMIT;
            getItems(true);
        }
    }
})

// if no filters have been set -> directly load products
onMounted(() => {
    document.addEventListener('scroll', onScroll);
    if (slots['filter-registration']({}).filter(filterItems).length === 0) {
        loadHash();
    }
});

onBeforeUnmount(() => {
    document.removeEventListener('scroll', onScroll);
})

async function getItems(append = false) {
    if (!append) {
        offset = 0;
    }
    if (loading.value) {
        // cancel ongoing requests
        controller.abort();
        loading.value = false;
        controller = new AbortController();
    }
    try {
        loading.value = false;
        error.value = false;
        let url = `${contextPath}${URL}/${props.workspace}/${siteName}/${lang}/${props.uuid}?limit=${LIMIT}&offset=${offset}`;
        if (filterString.value.length) {
            url += `&filter=${filterString.value}`;
        }
        const response = await fetch(url, { signal: controller.signal });
        if (response.status === 200) {
            result.value = (await response.json()) as OverviewResult;
            if (append) {
                // add to current result
                items.value.push(...result.value.results);
            } else {
                // replace result
                items.value = result.value.results;
            }
        } else {
            result.value = null;
            items.value = [];
        }
    } catch (e) {
        error.value = true;
    }
    loading.value = false;
}

function registerFilter(filter: OverviewFilter) {
    filters.value.push(filter);
    selectedFilters.value.push([]);
    selectedFiltersBuffer.value.push([]);
    // after all filters have been registered, set the filters from the url
    if (slots['filter-registration']({}).filter(filterItems).length === filters.value.length) {
        loadHash();
    }
}

function toggleFilter(filterKey: string, buffer = false, updateHash = true) {
    const selFilters = buffer ? selectedFiltersBuffer.value : selectedFilters.value;
    const filter = filters.value.find(f => f.categories.some(c => c.categoryKey === filterKey));
    if (!filter) {
        return;
    }
    const filterIndex = filters.value.findIndex(x => x.filterKey === filter.filterKey);
    const index = selFilters[filterIndex].indexOf(filterKey);
    if (index === -1) {
        if (updateHash) {
            addToState(filter.filterKey, filterKey);
        }
        selFilters[filterIndex].push(filterKey);
    } else {
        if (updateHash) {
            removeFromState(filter.filterKey, filterKey);
        }
        selFilters[filterIndex].splice(index, 1);
    }
    // if not on mobile (e.g. not using buffer) also fill it because it will override the filters on menu close
    if (!buffer) {
        selectedFiltersBuffer.value = [...selFilters];
    }
    getItems(false);
}

// check if a filter is selected or not (either in the buffer/mobile or regular)
function isSelected(key: string, buffer = false) {
    return (buffer ? selectedFiltersBuffer : selectedFilters).value.some(f =>
        f.some((x: string) => x === key)
    );
}

// close the filter menu without saving
function closeFilters() {
    filtersOpen.value = false;
    selectedFiltersBuffer.value = JSON.parse(JSON.stringify(selectedFilters.value));
    getItems();
}

// write filters selected in mobile to hash and close menu
function saveBuffer() {
    filters.value.forEach((filter: OverviewFilter, i: number) => {
        filter.categories.forEach((cat: OverviewCategory) => {
            // not in buffer but was selected before -> remove
            if (
                selectedFilters.value[i].includes(cat.categoryKey) &&
                !selectedFiltersBuffer.value[i].includes(cat.categoryKey)
            ) {
                removeFromState(filter.filterKey, cat.categoryKey);
            }
            if (
                !selectedFilters.value[i].includes(cat.categoryKey) &&
                selectedFiltersBuffer.value[i].includes(cat.categoryKey)
            ) {
                addToState(filter.filterKey, cat.categoryKey);
            }
        });
    });
    selectedFilters.value = [...selectedFiltersBuffer.value];
    filtersOpen.value = false;
}

// clear all filters selected in mobile
function clearBuffer() {
    selectedFiltersBuffer.value = selectedFilters.value.map(() => []);
}

// toggle filter menu. fills the buffer on opening and saves it on closing
function toggleFilters() {
    if (filtersOpen.value) {
        // since we have nested arrays we need a deep copy
        selectedFilters.value = JSON.parse(JSON.stringify(selectedFiltersBuffer.value));
    } else {
        selectedFiltersBuffer.value = JSON.parse(JSON.stringify(selectedFilters.value));
    }
    filtersOpen.value = !filtersOpen.value;
}

function onScroll() {
    if (el.value.getBoundingClientRect().bottom - window.innerHeight < 0) {
        if (!endReached.value) {
            endReached.value = true;
        }
    } else {
        if (endReached.value) {
            endReached.value = false;
        }
    }
}

// read hash from url, set the filters and make a request to get the items
async function loadHash(update = false) {
    const hash = location.hash.replace(/^#/, '');
    const _filters = hash
        .split('&')
        .filter(x => x.includes(':'))
        .map(x => x.split(':'))
        .filter(x => x.length === 2)
        .filter(f => filters.value.findIndex((x: OverviewFilter) => x.filterKey === f[0]) >= 0);

    _filters.forEach((f: string[], i: number) => {
        const filterCategory = filters.value.find((x: OverviewFilter) => x.filterKey === f[0]);
        if (filterCategory) {
            f[1].split(',').forEach((key: string) => {
                toggleFilter(key, false, update && i + 1 === _filters.length);
            });
        }
    });
    return getItems(false);
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "el",
    ref: el
  }, [
    _createElementVNode("div", _normalizeProps(_guardReactiveProps(_ctx.$attrs)), [
      (filters.value.length)
        ? _renderSlot(_ctx.$slots, "filterbutton", {
            key: 0,
            toggle: toggleFilters
          })
        : _createCommentVNode("", true),
      _createElementVNode("div", null, [
        _renderSlot(_ctx.$slots, "filter-rendering", {
          filters: filters.value,
          select: toggleFilter,
          show: filtersOpen.value,
          clear: clearBuffer,
          save: saveBuffer,
          close: closeFilters,
          isActive: isSelected
        }),
        _renderSlot(_ctx.$slots, "filter-registration")
      ])
    ], 16),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, "loading..."))
      : _renderSlot(_ctx.$slots, "default", {
          key: 1,
          items: items.value,
          total: result.value ? result.value.total : 0
        })
  ], 512))
}
}

})